/**
 * Pure mask rasterisation utilities for watermark inpainting.
 *
 * Nothing here touches the DOM, a canvas, or OpenCV — these functions operate
 * purely on typed arrays. That keeps the interesting logic unit testable under
 * jsdom without a canvas polyfill (the repo's vitest setup has none).
 *
 * A "mask" is a single-channel Uint8Array of width*height where 0 means
 * "known, keep this pixel" and non-zero means "hole, synthesise this pixel".
 * That is exactly the convention OpenCV's cv.inpaint expects.
 */

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function createMask(width: number, height: number): Uint8Array {
  return new Uint8Array(width * height);
}

/**
 * Separable square-kernel (Chebyshev) dilation via running prefix sums, so the
 * cost is O(width*height) regardless of how large `radius` is. A square kernel
 * rather than a true disk is deliberate: for our purposes we only need the mask
 * to comfortably *cover* the watermark so the synthesised region never shows a
 * hard seam at the star's faint outer edge. Chebyshev r and Euclidean r differ
 * by at most 41% at the corners, which is well inside the slack we leave.
 */
export function dilateMask(
  mask: Uint8Array,
  width: number,
  height: number,
  radius: number
): Uint8Array {
  if (radius <= 0) return mask.slice();
  const out = createMask(width, height);
  const horizontal = new Uint8Array(width * height);

  for (let y = 0; y < height; y++) {
    dilateRow(mask, horizontal, y * width, width, radius);
  }
  for (let x = 0; x < width; x++) {
    dilateColumn(horizontal, out, x, width, height, radius);
  }
  return out;
}

function dilateRow(
  src: Uint8Array,
  dst: Uint8Array,
  rowStart: number,
  width: number,
  radius: number
): void {
  const prefix = new Uint32Array(width + 1);
  for (let x = 0; x < width; x++) {
    prefix[x + 1] = prefix[x] + (src[rowStart + x] ? 1 : 0);
  }
  for (let x = 0; x < width; x++) {
    const lo = Math.max(0, x - radius);
    const hi = Math.min(width - 1, x + radius);
    dst[rowStart + x] = prefix[hi + 1] - prefix[lo] > 0 ? 1 : 0;
  }
}

function dilateColumn(
  src: Uint8Array,
  dst: Uint8Array,
  x: number,
  width: number,
  height: number,
  radius: number
): void {
  const prefix = new Uint32Array(height + 1);
  for (let y = 0; y < height; y++) {
    prefix[y + 1] = prefix[y] + (src[y * width + x] ? 1 : 0);
  }
  for (let y = 0; y < height; y++) {
    const lo = Math.max(0, y - radius);
    const hi = Math.min(height - 1, y + radius);
    dst[y * width + x] = prefix[hi + 1] - prefix[lo] > 0 ? 1 : 0;
  }
}

/** Tight bounding box of all set pixels, or null when the mask is empty. */
export function maskBounds(mask: Uint8Array, width: number, height: number): Box | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y++) {
    const rowStart = y * width;
    for (let x = 0; x < width; x++) {
      if (!mask[rowStart + x]) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

export function countMask(mask: Uint8Array): number {
  let total = 0;
  for (let i = 0; i < mask.length; i++) if (mask[i]) total++;
  return total;
}

/** Set every pixel within `radius` of (cx, cy). Clipped to the canvas. */
export function paintDisc(
  mask: Uint8Array,
  width: number,
  height: number,
  cx: number,
  cy: number,
  radius: number
): void {
  const r = Math.max(0.5, radius);
  const x0 = Math.max(0, Math.floor(cx - r));
  const x1 = Math.min(width - 1, Math.ceil(cx + r));
  const y0 = Math.max(0, Math.floor(cy - r));
  const y1 = Math.min(height - 1, Math.ceil(cy + r));
  const rSq = r * r;

  for (let y = y0; y <= y1; y++) {
    const dy = y - cy;
    const rowStart = y * width;
    for (let x = x0; x <= x1; x++) {
      const dx = x - cx;
      if (dx * dx + dy * dy <= rSq) mask[rowStart + x] = 255;
    }
  }
}

/** Rasterise a line segment as a swept disc, for brush strokes. */
export function paintSegment(
  mask: Uint8Array,
  width: number,
  height: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  radius: number
): void {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const steps = Math.ceil(Math.hypot(dx, dy));
  if (steps === 0) {
    paintDisc(mask, width, height, x0, y0, radius);
    return;
  }
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    paintDisc(mask, width, height, x0 + dx * t, y0 + dy * t, radius);
  }
}

/** Clear every pixel within `radius` of (cx, cy). Clipped to the canvas. */
export function eraseDisc(
  mask: Uint8Array,
  width: number,
  height: number,
  cx: number,
  cy: number,
  radius: number
): void {
  const r = Math.max(0.5, radius);
  const x0 = Math.max(0, Math.floor(cx - r));
  const x1 = Math.min(width - 1, Math.ceil(cx + r));
  const y0 = Math.max(0, Math.floor(cy - r));
  const y1 = Math.min(height - 1, Math.ceil(cy + r));
  const rSq = r * r;

  for (let y = y0; y <= y1; y++) {
    const dy = y - cy;
    const rowStart = y * width;
    for (let x = x0; x <= x1; x++) {
      const dx = x - cx;
      if (dx * dx + dy * dy <= rSq) mask[rowStart + x] = 0;
    }
  }
}

/** Erase a swept disc along a line segment. */
export function eraseSegment(
  mask: Uint8Array,
  width: number,
  height: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  radius: number
): void {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const steps = Math.ceil(Math.hypot(dx, dy));
  if (steps === 0) {
    eraseDisc(mask, width, height, x0, y0, radius);
    return;
  }
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    eraseDisc(mask, width, height, x0 + dx * t, y0 + dy * t, radius);
  }
}

export interface StarMaskOptions {
  /**
   * Single-channel alpha template, 0..255, length templateWidth*templateHeight.
   * Only the coverage silhouette matters here — unlike the old reverse-alpha
   * path we are not recovering blended pixel values from this, so the template
   * being a lossy JPEG capture no longer poisons the result.
   */
  template: Uint8Array;
  templateWidth: number;
  templateHeight: number;
  /** Star centre in image pixel coordinates. */
  centerX: number;
  centerY: number;
  /** Image pixels per template pixel. */
  scale: number;
  /** 0..1 fraction of the template peak to treat as "part of the watermark". */
  coverage: number;
  /** Dilation radius in image pixels, applied after thresholding. */
  grow: number;
}

/**
 * Rasterise the Gemini star template into a full-image hole mask.
 *
 * Uses nearest-neighbour (coordinate division) sampling rather than bilinear
 * resampling. Bilinear interpolation at the star's sharp tips manufactures
 * fractional alpha values that blur the silhouette and, historically, bled
 * correction into neighbouring content.
 *
 * Returns null when the resulting mask would be empty.
 */
export function buildStarMask(
  width: number,
  height: number,
  options: StarMaskOptions
): { mask: Uint8Array; box: Box } | null {
  const {
    template,
    templateWidth,
    templateHeight,
    centerX,
    centerY,
    scale,
    coverage,
    grow,
  } = options;

  if (scale <= 0 || templateWidth === 0 || templateHeight === 0) return null;

  // Threshold relative to the template's own peak rather than an absolute
  // constant, so this keeps working if the template is ever re-captured.
  let peak = 0;
  for (let i = 0; i < template.length; i++) {
    if (template[i] > peak) peak = template[i];
  }
  if (peak === 0) return null;

  const threshold = peak * Math.min(1, Math.max(0, coverage));

  const tHalfW = templateWidth / 2;
  const tHalfH = templateHeight / 2;

  // Bounding box of the star's influence in image space, clipped to the canvas.
  const reachX = tHalfW * scale + grow + 1;
  const reachY = tHalfH * scale + grow + 1;
  const left = Math.max(0, Math.floor(centerX - reachX));
  const right = Math.min(width - 1, Math.ceil(centerX + reachX));
  const top = Math.max(0, Math.floor(centerY - reachY));
  const bottom = Math.min(height - 1, Math.ceil(centerY + reachY));
  if (right < left || bottom < top) return null;

  const raw = createMask(width, height);

  for (let y = top; y <= bottom; y++) {
    const ty = Math.round((y - centerY) / scale + tHalfH - 0.5);
    if (ty < 0 || ty >= templateHeight) continue;
    const tRow = ty * templateWidth;
    const rowStart = y * width;
    for (let x = left; x <= right; x++) {
      const tx = Math.round((x - centerX) / scale + tHalfW - 0.5);
      if (tx < 0 || tx >= templateWidth) continue;
      if (template[tRow + tx] >= threshold) raw[rowStart + x] = 1;
    }
  }

  const grown = grow > 0 ? dilateMask(raw, width, height, Math.round(grow)) : raw;
  const box = maskBounds(grown, width, height);
  if (!box) return null;

  // Normalise to 255 for cv.inpaint; it only tests non-zero but a clean
  // 0/255 mask keeps debug overlays and tests unambiguous.
  for (let i = 0; i < grown.length; i++) grown[i] = grown[i] ? 255 : 0;

  return { mask: grown, box };
}

/**
 * Load a single-channel alpha channel out of an RGBA ImageData-ish buffer.
 * The Gemini template stores its alpha silhouette in the green channel on a
 * black background (it was captured from a dark render), so prefer green and
 * fall back to luminance when that looks empty.
 */
export function extractTemplateAlpha(
  rgba: Uint8ClampedArray,
  width: number,
  height: number
): Uint8Array {
  const green = new Uint8Array(width * height);
  const luma = new Uint8Array(width * height);
  let greenPeak = 0;
  let lumaPeak = 0;

  for (let i = 0, p = 0; p < width * height; p++, i += 4) {
    const g = rgba[i + 1];
    const l = (rgba[i] * 299 + rgba[i + 1] * 587 + rgba[i + 2] * 114) / 1000;
    green[p] = g;
    luma[p] = l;
    if (g > greenPeak) greenPeak = g;
    if (l > lumaPeak) lumaPeak = l;
  }

  // A pure-luminance template with a saturated colour cast would show a green
  // peak far below its luma peak; trust green only when it carries the signal.
  return greenPeak >= lumaPeak * 0.6 ? green : luma;
}